import { ExerciseTypeDTO, FirearmSpecificDTO, CourseDTO, MemberFirearmDTO, ExerciseSpecificDTO } from '../services/apiClient';
import { CourseTypeConstants } from '../constants/CourseTypeConstants';
import { isRifle, isHandgun } from '../constants/FirearmTypeConstants';

export interface FilteredExerciseData {
  exerciseTypes: ExerciseTypeDTO[];
  exerciseSpecifics: ExerciseSpecificDTO[];
}

export const filterExerciseTypesByCourseFirearmAndCaliber = (
  firearmSpecific: FirearmSpecificDTO,
  course: CourseDTO | undefined,
  selectedFirearm: MemberFirearmDTO,
): FilteredExerciseData => {
  if (
    !firearmSpecific?.exercises ||
    !course?.courseTypeId ||
    !selectedFirearm?.firearmTypeId ||
    !selectedFirearm?.firearmCaliberId
  ) {
    return { exerciseTypes: [], exerciseSpecifics: [] };
  }

  const firearmTypeId = selectedFirearm.firearmTypeId;

  const isFirearmRifle = isRifle.includes(firearmTypeId ?? '');
  const isFirearmHandgun = isHandgun.includes(firearmTypeId ?? '');

  // Step 1: Check if firearm is allowed for this course type
  let isFirearmAllowed = false;

  switch (course?.courseTypeId) {
    case CourseTypeConstants.DEDICATED_HUNTER:
      isFirearmAllowed = isFirearmRifle;
      break;

    case CourseTypeConstants.DEDICATED_SPORTS_PERSON:
      isFirearmAllowed = isFirearmHandgun || isFirearmRifle;
      break;

    case CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON:
      isFirearmAllowed = isFirearmHandgun || isFirearmRifle;
      break;

    default:
      isFirearmAllowed = false;
  }

  if (!isFirearmAllowed) {
    return { exerciseTypes: [], exerciseSpecifics: [] };
  }

  // Step 2: Filter exercises and specifics based on evaluation flags
  const allowedExerciseTypes: ExerciseTypeDTO[] = [];
  const allowedExerciseSpecifics: ExerciseSpecificDTO[] = [];

  firearmSpecific.exercises.forEach((exercise: any) => {
    const exerciseType = exercise.exerciseType;

    // Filter exercise specifics based on evaluation flags
    const validExerciseSpecifics = exercise.exerciseSpecifics?.filter((specific: any) => {
      switch (course?.courseTypeId) {
        case CourseTypeConstants.DEDICATED_HUNTER:
          return specific.isEvaluationHunt === true;

        case CourseTypeConstants.DEDICATED_SPORTS_PERSON:
          return specific.isEvaluationSport === true;

        case CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON:
          return specific.isEvaluationHunt === true || specific.isEvaluationSport === true;

        default:
          return false;
      }
    });

    if (validExerciseSpecifics && validExerciseSpecifics.length > 0) {
      // Add to allowed specifics
      allowedExerciseSpecifics.push(...validExerciseSpecifics);

      // Check if we already have this exercise type (avoid duplicates)
      const alreadyExists = allowedExerciseTypes.some(existing => existing.id === exerciseType.id);
      if (!alreadyExists) {
        allowedExerciseTypes.push(exerciseType);
      }
    }
  });

  return {
    exerciseTypes: allowedExerciseTypes,
    exerciseSpecifics: allowedExerciseSpecifics
  };
};

export const filterExerciseTypesByCompetition = (
  firearmSpecific: FirearmSpecificDTO,
  _course: CourseDTO | undefined,
  _selectedFirearm: MemberFirearmDTO,
): FilteredExerciseData => {
  if (!firearmSpecific?.exercises) {
    return { exerciseTypes: [], exerciseSpecifics: [] };
  }

  const allowedExerciseTypes: ExerciseTypeDTO[] = [];
  const allowedExerciseSpecifics: ExerciseSpecificDTO[] = [];

  firearmSpecific.exercises.forEach((exercise: any) => {
    if (exercise.exerciseType?.isCompetition !== true) return;

    const validSpecifics = exercise.exerciseSpecifics?.filter(
      (specific: any) => specific.isCompetition === true,
    );

    if (validSpecifics && validSpecifics.length > 0) {
      allowedExerciseSpecifics.push(...validSpecifics);

      const alreadyExists = allowedExerciseTypes.some(e => e.id === exercise.exerciseType?.id);
      if (!alreadyExists) {
        allowedExerciseTypes.push(exercise.exerciseType);
      }
    }
  });

  return { exerciseTypes: allowedExerciseTypes, exerciseSpecifics: allowedExerciseSpecifics };
};